export const OWNER_ANALYTICS_ROUTE = '/sole-con-mana-ment-ge'

export type ProductSectionAnalytics = {
  key: string
  label: string
  surface: string
}

export const SECTION_LABELS: Record<string, string> = {
  AI_MAGIC: 'AI Magic',
  AUTOMATE: 'Automate',
  BUDGET: 'Budget',
  CALENDAR: 'Calendar',
  CHAT: 'Chat',
  CHECKLISTS: 'Checklists',
  CONTROLS: 'Controls',
  DASHBOARD: 'Dashboard',
  DESIGN: 'Design',
  FIELD_PASS: 'Field Pass',
  FOCUS: 'Focus',
  FORMS: 'Forms',
  FOR_YOU: 'For You',
  HELPER_DASHBOARD: 'Helper Dashboard',
  INVITES: 'Team',
  LINK_HUB: 'Link Hub',
  MANAGE: 'Manage',
  MEETINGS: 'Meetings',
  MEMBER_FOR_YOU: 'Member For You',
  OVERVIEW: 'Overview',
  PROFILE: 'Profile',
  PUBLIC_SITE: 'Public Site',
  PURCHASE: 'Purchase',
  QUOTA: 'Quota',
  TEACHING: 'Teaching',
  TRAINING: 'Training',
  RAID: 'Raid',
  REPORTS: 'Reports',
  SETTINGS: 'Settings',
  STOCK: 'Stock',
  SUMMARY: 'Audit Summary',
  TEAM: 'Team',
  TIMELINE: 'Timeline',
  UPLOADHUB: 'UploadHub',
  YOUR_PAGE: 'Your Page',
}

const DESKTOP_TOP_LEVEL: Record<string, string> = {
  explore: 'EXPLORE',
  focus: 'FOCUS',
  invites: 'INVITES',
  members: 'INVITES',
  team: 'INVITES',
  purchase: 'PURCHASE',
  settings: 'SETTINGS',
  your: 'YOUR_PAGE',
}

const WORKSPACE_TOP_LEVEL: Record<string, string> = {
  automate: 'AUTOMATE',
  budget: 'BUDGET',
  calendar: 'CALENDAR',
  chat: 'CHAT',
  design: 'DESIGN',
  forms: 'FORMS',
  'for-you': 'FOR_YOU',
  link: 'LINK_HUB',
  meetings: 'MEETINGS',
  overview: 'OVERVIEW',
  quota: 'QUOTA',
  teaching: 'TEACHING',
  training: 'TRAINING',
  stock: 'STOCK',
  upload: 'UPLOADHUB',
  uploads: 'UPLOADHUB',
}

const BOARD_LEVEL: Record<string, string> = {
  ai: 'AI_MAGIC',
  checklists: 'CHECKLISTS',
  manage: 'MANAGE',
  raid: 'RAID',
  reports: 'REPORTS',
}

const stripConsolePrefix = (pathname: string) => {
  if (pathname === '/console') return '/'
  if (pathname.startsWith('/console/')) return pathname.slice('/console'.length) || '/'
  return pathname
}

const pathOnly = (path: string) => {
  const queryIndex = path.indexOf('?')
  const hashIndex = path.indexOf('#')
  const cut = [queryIndex, hashIndex].filter((index) => index >= 0).sort((a, b) => a - b)[0]
  return cut == null ? path : path.slice(0, cut)
}

const section = (key: string, surface: string): ProductSectionAnalytics => ({
  key,
  label: SECTION_LABELS[key] ?? key,
  surface,
})

export const deriveProductSectionFromPath = (pathname: string): ProductSectionAnalytics | null => {
  const normalized = stripConsolePrefix((pathOnly(pathname) || '/').toLowerCase())
  if (normalized === OWNER_ANALYTICS_ROUTE) return null
  if (normalized === '/' || normalized === '/console') return section('DASHBOARD', 'desktop')
  const parts = normalized.split('/').filter(Boolean)
  const [root] = parts

  if (root === 'helper') {
    const tab = parts[1] ?? 'dashboard'
    if (tab === 'chat' || tab === 'nexus') return section('CHAT', 'helper')
    if (tab === 'calendar') return section('CALENDAR', 'helper')
    if (tab === 'meetings') return section('MEETINGS', 'helper')
    if (tab === 'controls' || tab === 'scan') return section('CONTROLS', 'helper')
    if (tab === 'profile') return section('PROFILE', 'helper')
    return section('HELPER_DASHBOARD', 'helper')
  }

  if (root === 'member') {
    const tab = parts[2] ?? 'for-you'
    if (tab === 'for-you') return section('MEMBER_FOR_YOU', 'member')
    if (tab === 'chat' || tab === 'nexus') return section('CHAT', 'member')
    if (tab === 'calendar' || tab === 'meetings') return section('CALENDAR', 'member')
    if (tab === 'controls') return section('CONTROLS', 'member')
    if (tab === 'you') return section('YOUR_PAGE', 'member')
    if (tab === 'board') return section('TIMELINE', 'member')
    return section('TIMELINE', 'member')
  }

  if (root === 'field-pass') return section('FIELD_PASS', 'field-pass')
  if (root === 'invite' || root === 'invite-review' || root === 'join') return section('INVITES', 'public')

  if (root === 'workspace' || root === 'personal') {
    const firstFeature = root === 'workspace' ? parts[2] : parts[1]
    const secondFeature = root === 'workspace' ? parts[3] : parts[2]
    if (firstFeature === 'manage' && secondFeature === 'audit') return section('SUMMARY', 'desktop')
    const topLevel = WORKSPACE_TOP_LEVEL[firstFeature]
    if (topLevel) return section(topLevel, 'desktop')
    const boardLevel = BOARD_LEVEL[secondFeature]
    if (boardLevel) return section(boardLevel, 'desktop')
    return section('TIMELINE', 'desktop')
  }

  const topLevel = DESKTOP_TOP_LEVEL[root]
  if (topLevel) return section(topLevel, root === 'explore' ? 'public' : 'desktop')
  if (['solutions', 'docs', 'resources', 'pricing', 'about', 'faq', 'contact'].includes(root)) return section('PUBLIC_SITE', 'public')
  return section('TIMELINE', 'desktop')
}
